export const useSuperchat = () => {
  const isInitialized = ref(false)
  const isWidgetInitialized = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const isConsentCookieAvailable = computed(() => {
    if (import.meta.server) return false
    const consentCookie = useCookie('cc')
    return !!consentCookie.value
  })

  const initializeSuperchat = async () => {
    if (import.meta.server) return

    if (!isConsentCookieAvailable.value) {
      error.value = 'No consent cookie'
      return
    }

    if (isInitialized.value) {
      return
    }

    const { ENV, SUPERCHAT_APP_KEY: appKey } = useRuntimeConfig().public

    if (ENV === 'development' || ENV === 'staging' || ENV === 'production') {
      return
    }

    if (!appKey) {
      error.value = 'Superchat app key not configured'
      return
    }

    isLoading.value = true
    error.value = null

    try {
      // Check if script is already loaded
      if (
        document.querySelector(
          'script[src="https://widget.superchat.de/sdk.js"]'
        )
      ) {
        isInitialized.value = true
        isLoading.value = false
        return
      }

      // Load Superchat widget script
      const script = document.createElement('script')
      script.src = 'https://widget.superchat.de/sdk.js'
      script.async = true

      await new Promise<void>((resolve, reject) => {
        script.onload = () => {
          isInitialized.value = true
          resolve()
        }
        script.onerror = () => {
          error.value = 'Failed to load Superchat widget'
          reject(new Error('Failed to load Superchat widget'))
        }
        document.head.appendChild(script)
      })
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error'
    } finally {
      isLoading.value = false
    }
  }

  const initializeWidget = (appKey: string) => {
    if (import.meta.server) return
    if (isWidgetInitialized.value) return
    if (!isInitialized.value) return

    if (typeof window !== 'undefined' && 'Superchat' in window) {
      const superchat = (window as any).Superchat
      try {
        superchat.init({
          applicationKey: appKey,
          hideWidget: false,
          language: 'de',
        })
        isWidgetInitialized.value = true

        setTimeout(() => {
          if (superchat.show) {
            superchat.show()
          }
        }, 1000)
      } catch (err) {
        error.value =
          err instanceof Error ? err.message : 'Widget initialization failed'
      }
    }
  }

  const destroySuperchat = () => {
    if (import.meta.server) return
    if (!isInitialized.value) return

    // Hide widget if it exists
    if (typeof window !== 'undefined' && 'Superchat' in window) {
      const superchat = (window as any).Superchat
      if (superchat && typeof superchat.hide === 'function') {
        superchat.hide()
      }
    }

    // Remove script from DOM
    const script = document.querySelector(
      'script[src="https://widget.superchat.de/sdk.js"]'
    )
    if (script) {
      script.remove()
    }

    // Clear any global Superchat objects
    if (typeof window !== 'undefined' && 'Superchat' in window) {
      delete (window as any).Superchat
    }

    isInitialized.value = false
    isWidgetInitialized.value = false
    error.value = null
  }

  // Watch for consent changes
  watch(
    isConsentCookieAvailable,
    (hasConsent) => {
      if (hasConsent) {
        initializeSuperchat()
      } else {
        destroySuperchat()
      }
    },
    { immediate: true }
  )

  // Also watch for the cookie directly to catch changes
  if (import.meta.client) {
    const consentCookie = useCookie<{ advertising?: boolean }>('cc')
    watch(
      consentCookie,
      () => {
        isConsentCookieAvailable.value
      },
      { deep: true }
    )
  }

  return {
    isInitialized: readonly(isInitialized),
    isWidgetInitialized: readonly(isWidgetInitialized),
    isLoading: readonly(isLoading),
    error: readonly(error),
    isConsentCookieAvailable: readonly(isConsentCookieAvailable),
    initializeSuperchat,
    initializeWidget,
    destroySuperchat,
  }
}
